import { useState } from "react";
import { BookOpen, X, CheckCircle, Copy, Check, RotateCcw, Circle } from "lucide-react";
import { renderMarkdown } from "../utils/markdown";

/**
 * The shared build-output panel. One component, two states tied to item status:
 *
 *   status="locked" — read-only confirmed content, "Locked & confirmed" tag,
 *                     Copy and Revise. This is the Accelerator treatment.
 *                     A `nicheStamp.stale` locked item is the same panel with
 *                     the tag reading "Past locked" — she finished the work, it
 *                     was just built for a niche she has left.
 *   status="draft"  — the same shell and geometry, but the body becomes an
 *                     editable form and the footer offers Save / Cancel.
 *
 * Momentum items are meant to reuse this rather than getting a separate editing
 * screen, which is why the draft form is driven by a `fields` array instead of
 * hardcoding offer fields. Mounts full-page on mobile, as a side panel on
 * desktop. Nothing passes status="draft" yet — Momentum will.
 *
 * Colours come from canvas tokens, so the body follows the panel-mode toggle.
 * The header uses chrome tokens on purpose: it reads as part of the same dark
 * band as the sidebar and does not change with the toggle.
 */
export function OutputPanel({
  promptName, content, onClose, onRevise, isMobile, moduleLabel,
  status = "locked",
  fields, onSave, onCancel,
  /* { label, date, stale } — which niche this was locked under. Null when the
     coach has no niche versions yet, in which case the line is simply absent. */
  nicheStamp,
}) {
  const [copied, setCopied] = useState(false);
  const [values, setValues] = useState(() =>
    Object.fromEntries((fields || []).map((f) => [f.key, f.value ?? ""]))
  );

  const isDraft = status === "draft";
  /* Stored, but under a niche she has left. Not a draft — she finished this —
     and not current either, which is exactly the state the header used to have
     no word for. */
  const isPast = !isDraft && !!nicheStamp?.stale;

  const handleCopy = () => {
    const plain = content.replace(/#{1,3} /gm, "").replace(/\*\*([^*]+)\*\*/g, "$1").replace(/\*([^*]+)\*/g, "$1");
    navigator.clipboard.writeText(plain).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const btnBase = {
    padding: isMobile ? "13px" : "10px",
    borderRadius: 10, fontSize: isMobile ? 14 : 13, fontWeight: 600,
    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
    touchAction: "manipulation", transition: "all 0.15s",
  };

  const inputBase = {
    width: "100%", boxSizing: "border-box",
    padding: "10px 12px", borderRadius: 8,
    background: "var(--input-bg)",
    border: "1px solid var(--border-strong)",
    color: "var(--text-primary)",
    fontSize: isMobile ? 14 : 13, fontFamily: "inherit", lineHeight: 1.5,
    outline: "none",
  };

  return (
    <div style={isMobile ? {
      position: "fixed", inset: 0, zIndex: 60, background: "var(--bg-surface)", display: "flex", flexDirection: "column",
    } : {
      width: 340, background: "var(--bg-surface)", borderLeft: "1px solid var(--border-subtle)",
      display: "flex", flexDirection: "column", height: "100vh", flexShrink: 0,
      animation: "slideIn 0.2s ease",
    }}>
      <div style={{
        background: "var(--chrome-bg)", padding: "14px 16px",
        borderBottom: `3px solid ${isDraft ? "var(--status-draft)" : "var(--chrome-accent-solid)"}`,
        flexShrink: 0,
      }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 3 }}>
              <BookOpen size={13} color="var(--chrome-accent)" />
              <span style={{ color: "var(--chrome-muted)", fontSize: 11, letterSpacing: "0.5px", textTransform: "uppercase" }}>{moduleLabel}</span>
            </div>
            <h3 style={{ color: "var(--chrome-text)", margin: 0, fontSize: 15, fontWeight: 700 }}>{promptName}</h3>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}>
            <X size={16} color="var(--chrome-muted)" />
          </button>
        </div>
        {/* THREE SURFACES, ONE STATE, AND THEY DISAGREED. The chat card has said
            "Past locked" since sectionLockState shipped, and the sidebar now
            ticks a past section and marks it. This header went on saying
            "Locked & confirmed" in teal for the same content, qualified only by
            a line underneath — which was ALSO teal, the colour that means
            confirmed everywhere else in the app. So the loudest thing on the
            panel told her the work was current.

            It reads "Past locked" now, in the same words and the same muted tone
            as the card in the chat, and the qualifier goes amber: attention, not
            confirmation. Teal is action, amber is attention. */}
        <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 5 }}>
          {isDraft
            ? <Circle size={11} color="var(--status-draft)" />
            : <CheckCircle size={11} color={isPast ? "var(--chrome-muted)" : "var(--chrome-accent)"} />
          }
          <span style={{
            color: isDraft ? "var(--status-draft)" : isPast ? "var(--chrome-muted)" : "var(--chrome-accent)",
            fontSize: 11, fontWeight: 600,
          }}>
            {isDraft ? "Draft — not locked yet" : isPast ? "Past locked" : "Locked & confirmed"}
          </span>
        </div>
        {!isDraft && nicheStamp && (
          <div style={{ marginTop: 6, fontSize: 10.5, lineHeight: 1.5, color: "var(--chrome-muted)" }}>
            Built on: {nicheStamp.label}
            {nicheStamp.date && ` · ${new Date(nicheStamp.date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`}
            {nicheStamp.stale && (
              <span style={{ color: "var(--brand-amber)", fontWeight: 600 }}> · not your current niche</span>
            )}
          </div>
        )}
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "18px 16px", WebkitOverflowScrolling: "touch" }}>
        {isDraft ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {(fields || []).map((f) => (
              <div key={f.key}>
                <label style={{
                  display: "block", marginBottom: 6, fontSize: 11, fontWeight: 700,
                  textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--text-muted)",
                }}>
                  {f.label}
                </label>
                {f.type === "textarea" ? (
                  <textarea
                    value={values[f.key]}
                    placeholder={f.placeholder}
                    onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                    style={{ ...inputBase, minHeight: 96, resize: "vertical" }}
                  />
                ) : f.type === "select" ? (
                  <select
                    value={values[f.key]}
                    onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                    style={inputBase}
                  >
                    {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input
                    value={values[f.key]}
                    placeholder={f.placeholder}
                    onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                    style={inputBase}
                  />
                )}
              </div>
            ))}
          </div>
        ) : (
          <div style={{
            fontSize: 13, lineHeight: 1.85, color: "var(--text-primary)", wordBreak: "break-word",
            borderLeft: "3px solid var(--accent)", paddingLeft: 14,
          }}>
            {renderMarkdown(content)}
          </div>
        )}
      </div>

      <div style={{ padding: isMobile ? "16px" : "12px 16px", borderTop: "1px solid var(--border-panel)", flexShrink: 0, background: "var(--bg-surface)" }}>
        {isDraft ? (
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={onCancel}
              style={{
                ...btnBase, flex: 1,
                border: "1px solid var(--border-strong)",
                background: "var(--bg-primary)", color: "var(--text-secondary)",
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => onSave?.(values)}
              style={{
                ...btnBase, flex: 2,
                border: "none",
                background: "var(--btn-bg)", color: "var(--btn-text)",
              }}
            >
              <Check size={13} />
              Save
            </button>
          </div>
        ) : (
          <>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={handleCopy}
                style={{
                  ...btnBase, flex: 1,
                  border: `1px solid ${copied ? "var(--accent)" : "var(--border-strong)"}`,
                  background: copied ? "var(--accent-quiet)" : "var(--bg-primary)",
                  color: copied ? "var(--accent)" : "var(--text-secondary)",
                }}
              >
                {copied ? <Check size={13} /> : <Copy size={13} />}
                {copied ? "Copied!" : "Copy"}
              </button>
              <button
                onClick={onRevise}
                style={{
                  ...btnBase, flex: 2,
                  border: "1px solid var(--accent-line)",
                  background: "var(--accent-quiet)", color: "var(--text-primary)",
                }}
              >
                <RotateCcw size={13} />
                Revise
              </button>
            </div>
            <p style={{ margin: "8px 0 0", fontSize: 11, color: "var(--text-disabled)", textAlign: "center" }}>Saved to your account</p>
          </>
        )}
      </div>
    </div>
  );
}
